import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { OpsEnums } from '@ops/global-config';
import { environment } from '@ops/environments';

@Injectable({
  providedIn: 'root',
})
export class CommunicationManagerService implements OnInit, OnDestroy {
  private userToken: string | undefined;
  private _enums: any = {};

  baseUrl = environment.apiUrl;
  private _headers: HttpHeaders = new HttpHeaders().set(
    'content-type',
    'application/json'
  );
  ipcRenderer: any;

  constructor(private httpClient: HttpClient) {
    // console.log('constructed Comms Manager');
    if ((window as any).require) {
      // eslint-disable-next-line no-useless-catch
      try {
        // this.ipcRenderer = (window as any).require('electron').ipcRenderer;
      } catch (e) {
        throw e;
      }
    } else {
      // console.warn('Electron IPC was not loaded');
    }
  }
  ngOnDestroy(): void {
    this.userToken = undefined;
    this._enums = {};
  }

  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  ngOnInit() {
    console.log('CommunicationManagerService initialized with baseUrl:', this.baseUrl);
    if (this.enums.length === 0) {
      this.get('enums')
        .pipe(
          switchMap((enums) => {
            OpsEnums.setEnums(enums);
            return enums;
          })
        )
        .subscribe((enums) => {
          this.enums(enums);
        });
    }

   
  }

  public get isElectron(): boolean {
    const windowTempCover = window as any;

    return !!(
      windowTempCover &&
      windowTempCover?.process &&
      windowTempCover?.process?.type
    );
  }

  public on(channel: string, listener: any): void {
    if (!this.ipcRenderer) {
      // console.log('IPC not loaded');
      return;
    }

    this.ipcRenderer.on(channel, listener);
  }

  public async send(channel: string, ...args: any[]): Promise<any> {
    if (!this.ipcRenderer) {
      // console.log('IPC not loaded');
      return;
    }
    this.ipcRenderer.send(channel, ...args);
  }

  public request = async (channel: any, payload: any): Promise<any> => {
    try {
      // console.log('ipcRenderer:', this.ipcRenderer);
    } catch (error: any) {
      throw new Error(CommunicationManagerService.trimError(error?.message));
    }
  };

  //TODO add session manager
  public setUserToken(token: string) {
    //token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI2MjZmYzUyMGRhNGY4MGJlMGJjZjYzNmUiLCJhdWQiOiJPUFMxMSIsImlhdCI6MTY2OTczODc0MCwiZXhwIjoxNzAxMjc0NzQwfQ.DJhwS6vReGM9OixpQsmN7fpA0Kx9Vl164v1CLWqZGQ4';
    this.userToken = `Bearer ${token}`;
    this._headers = this._headers.set('Authorization', this.userToken);
  }

  public isUserLoggedin(): boolean {
    return !(!this.userToken || this.userToken.length === 0);
  }

  protected static trimError = (errorMessage: string): string =>
    errorMessage.replace(/Error invoking remote method '.*': Error:/, '');

  public get(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<any> {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.get(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
    });
  }

  public getAndExpectText(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<any> {
    const newHeaders = this.createHeaders('text', addHeaders ?? []);
    return this.httpClient.get(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
      observe: 'response',
      responseType: 'text',
    });
  }

  public getVoid(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<any> {
    const newHeaders = this.createHeaders('', addHeaders ?? []);
    return this.httpClient.get(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
    });
  }

  public get enums() {
    return this._enums;
  }

  public set enums(enumsValue: any) {
    this._enums = enumsValue;
  }

  public getWithBody(
    route: string,
    payload: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<any> {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    const request = new HttpRequest('GET', route, payload, {
      headers: newHeaders,
      responseType: 'json',
    });
    return this.httpClient.request(request);
  }

  public patch(
    route: string,
    payload?: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<string> {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.patch(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders,
      responseType: 'text',
    });
  }

  public post(
    route: string,
    payload?: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<string> {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.post(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders,
      responseType: 'text',
    });
  }

  public put(
    route: string,
    payload?: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.put(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders,
    });
  }

  public putArchive(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.put(`${this.baseUrl}/${route}`, null, {
      headers: newHeaders,
      responseType: 'text',
    });
  }

  public delete(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.delete(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
      responseType: 'text',
    });
  }

  public deleteMultiple(
    route: string,
    payload: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);
    return this.httpClient.delete(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
      body: payload,
      responseType: 'text',
    });
  }

  public postAndGetJson(
    route: string,
    payload?: any,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ): Observable<any> {
    const newHeaders = this.createHeaders('json', addHeaders ?? []);

    return this.httpClient.post<any>(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders ?? null,
    });
  }

  public uploadFileAndGetJson({
    route,
    payload,
    addHeaders,
  }: {
    route: string;
    payload?: any;
    addHeaders?: Array<{ headerName: string; headerValue: string }>;
  }): Observable<any> {
    const newHeaders = this.createHeaders('file', addHeaders ?? []);

    return this.httpClient.put<any>(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders,
    });
  }

  public uploadAndGetJson({
    route,
    payload,
    addHeaders,
  }: {
    route: string;
    payload?: any;
    addHeaders?: Array<{ headerName: string; headerValue: string }>;
  }): Observable<any> {
    const newHeaders = this.createHeaders('file', addHeaders ?? []);

    return this.httpClient.post<any>(`${this.baseUrl}/${route}`, payload, {
      headers: newHeaders,
    });
  }

  public downloadFile(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    let newHeaders = this.createHeaders('file', [
      { headerName: 'content-type', headerValue: 'multipart/form-data' },
    ]);

    addHeaders?.forEach((h) => {
      newHeaders = newHeaders.set(h.headerName, h.headerValue);
    });

    return this.httpClient.get(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
      responseType: 'blob',
    });
  }

  public previewFile(
    route: string,
    addHeaders?: Array<{ headerName: string; headerValue: string }>
  ) {
    let newHeaders = this.createHeaders('', addHeaders ?? []);
    return this.httpClient.get(`${this.baseUrl}/${route}`, {
      headers: newHeaders,
      responseType: 'text',
    });
  }

  private createHeaders(
    contentType: string,
    addHeaders: Array<{ headerName: string; headerValue: string }>
  ): HttpHeaders {
    let newHeaders: HttpHeaders = new HttpHeaders();
    if (this.userToken)
      newHeaders = new HttpHeaders({
        Authorization: this.userToken,
      });

    switch (contentType) {
      case 'json':
        newHeaders = newHeaders.set('content-type', 'application/json');
        break;
      case 'file':
        //newHeaders = newHeaders.set('content-type', 'multipart/form-data');
        break;
      case 'text':
        newHeaders = newHeaders.set('content-type', 'text/plain');
        break;
      default:
        break;
    }

    addHeaders?.forEach((h) => {
      newHeaders = newHeaders.set(h.headerName, h.headerValue);
    });
    return newHeaders;
  }
}
